import { useContext, useRef } from "react";
import { useQuery } from "react-query";
import AuthContext from "contexts/AuthContext";

const { REACT_APP_API } = process.env;

const retrieveGiftcard = async ({ authTokens, payload }) => {
  const response = await fetch(`${REACT_APP_API}/giftcard/retrieve-giftcard`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authTokens.access_token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to retrieve giftcard");
  }

  return response.json();
};

// Helper function to check if giftcard data contains valid code
const hasValidGiftcardCode = (data) => {
  const metadata = data?.data?.[0]?.payments_metadatas?.items;
  if (!metadata) return false;

  const metadataMap = metadata.reduce((acc, item) => {
    acc[item.key] = item.value;
    return acc;
  }, {});

  return !!metadataMap.gift_card_code;
};

export const useRetrieveGiftcard = ({ takebackId }, options = {}) => {
  const { authTokens } = useContext(AuthContext);
  const attemptCountRef = useRef(0);
  const maxAttempts = 10;
  const pollingInterval = 3000; // 3 seconds

  return useQuery({
    queryKey: ["retrieveGiftcard", takebackId],
    queryFn: () => {
      const freshTokens = JSON.parse(localStorage.getItem("authTokens"));
      attemptCountRef.current += 1;
      return retrieveGiftcard({
        authTokens: { access_token: freshTokens?.access_token },
        payload: { takebackId },
      });
    },
    enabled: !!authTokens && !!takebackId,
    refetchInterval: (data) => {
      // Stop polling if we have valid giftcard code or exceeded max attempts
      if (
        hasValidGiftcardCode(data) ||
        attemptCountRef.current >= maxAttempts
      ) {
        return false;
      }
      // Continue polling every 5 seconds
      return pollingInterval;
    },
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      // Reset attempt counter on successful fetch with valid data
      if (hasValidGiftcardCode(data)) {
        attemptCountRef.current = 0;
      }
    },
    onError: () => {
      // Reset attempt counter on error so we can retry fresh next time
      attemptCountRef.current = 0;
    },
    ...options,
  });
};
